// frontend/src/components/shared/modals/EnrollmentUpdateWarningModal.tsx
import { useState } from 'react';
import { X, AlertTriangle, Info, RefreshCw } from 'lucide-react';
import { getSemesterDisplayName } from '../../../utils/semesterUtils';
import { useEscapeKey } from '../../../hooks/useEscapeKey';

export type EnrollmentUpdateOption = 'profile-only' | 'reset-plan';

export interface EnrollmentUpdateWarningModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentEnrollment: string | null | undefined;
  newEnrollment: string | null | undefined;
  onOptionSelect: (option: EnrollmentUpdateOption) => void;
}

const EnrollmentUpdateWarningModal = ({
  isOpen,
  onClose,
  currentEnrollment,
  newEnrollment,
  onOptionSelect,
}: EnrollmentUpdateWarningModalProps) => {
  const [selectedOption, setSelectedOption] = useState<EnrollmentUpdateOption | ''>('');
  const [isProcessing, setIsProcessing] = useState(false);

  useEscapeKey(() => {
    if (!isProcessing) onClose();
  }, isOpen);

  const formatSemester = getSemesterDisplayName;

  const handleContinue = () => {
    if (!selectedOption) return;

    setIsProcessing(true);
    onOptionSelect(selectedOption);
  };

  if (!isOpen) return null;

  return (
    <>
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-50 transition-opacity"
        onClick={onClose}
      />

      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-xl w-full max-w-lg transform transition-all">
          <div className="flex items-center justify-between p-6 border-b border-line-soft bg-status-warning/10">
            <div className="flex items-center space-x-2">
              <AlertTriangle className="h-6 w-6 text-yellow-600" />
              <h2 className="text-xl font-semibold text-gray-900">
                Update Enrollment Start Semester?
              </h2>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500 transition-colors"
              disabled={isProcessing}
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          <div className="p-6">
            <div className="mb-6 p-4 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-600">
                You're changing your enrollment from{' '}
                <span className="font-semibold text-gray-900">
                  {formatSemester(currentEnrollment)}
                </span>{' '}
                to{' '}
                <span className="font-semibold text-gray-900">
                  {formatSemester(newEnrollment)}
                </span>
              </p>
            </div>

            <div className="space-y-4">
              <p className="text-sm font-medium text-gray-700 mb-3">
                Please choose how to proceed:
              </p>

              <label className="relative flex items-start p-4 border rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                <input
                  type="radio"
                  name="update-option"
                  value="profile-only"
                  checked={selectedOption === 'profile-only'}
                  onChange={(e) => setSelectedOption(e.target.value as EnrollmentUpdateOption)}
                  className="mt-1"
                  disabled={isProcessing}
                />
                <div className="ml-3 flex-1">
                  <div className="flex items-center space-x-2">
                    <Info className="h-5 w-5 text-primary" />
                    <span className="font-medium text-gray-900">Just update my profile</span>
                    <span className="text-xs text-green-700 dark:text-green-300 font-medium bg-success/10 px-2 py-1 rounded-full">
                      Recommended
                    </span>
                  </div>
                  <ul className="mt-2 text-sm text-gray-600 space-y-1">
                    <li>• Changes the displayed start date in your profile</li>
                    <li>• Keeps your current academic plan unchanged</li>
                    <li>• No courses or semesters are affected</li>
                  </ul>
                </div>
              </label>

              <label className="relative flex items-start p-4 border border-status-critical/25 rounded-card cursor-pointer hover:bg-status-critical/10 transition-colors">
                <input
                  type="radio"
                  name="update-option"
                  value="reset-plan"
                  checked={selectedOption === 'reset-plan'}
                  onChange={(e) => setSelectedOption(e.target.value as EnrollmentUpdateOption)}
                  className="mt-1"
                  disabled={isProcessing}
                />
                <div className="ml-3 flex-1">
                  <div className="flex items-center space-x-2">
                    <RefreshCw className="h-5 w-5 text-red-600" />
                    <span className="font-medium text-gray-900">Reset my academic plan</span>
                    <span className="text-xs text-status-critical-fg font-medium bg-status-critical/10 px-2 py-1 rounded-full">
                      Destructive
                    </span>
                  </div>
                  <div className="mt-2 bg-status-critical/10 p-3 rounded-card">
                    <p className="text-sm font-medium text-red-800 mb-1">
                      ⚠️ Warning: This action cannot be undone!
                    </p>
                    <ul className="text-sm text-red-700 space-y-1">
                      <li>• Creates new semesters starting {formatSemester(newEnrollment)}</li>
                      <li>
                        • <strong>DELETES all current courses</strong> from your plan
                      </li>
                      <li>• Generates a fresh full-length plan from your start term</li>
                    </ul>
                  </div>
                </div>
              </label>
            </div>

            {selectedOption === 'reset-plan' && (
              <div className="mt-4 p-3 bg-status-warning/10 border border-status-warning/25 rounded-card">
                <p className="text-sm text-yellow-800">
                  <strong>Are you sure?</strong> All your course selections will be permanently
                  deleted. This option is mainly for testing purposes.
                </p>
              </div>
            )}
          </div>

          <div className="flex items-center justify-end space-x-2 px-6 py-4 border-t bg-gray-50">
            <button
              type="button"
              onClick={onClose}
              disabled={isProcessing}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleContinue}
              disabled={!selectedOption || isProcessing}
              className={`px-4 py-2 text-sm font-medium text-white rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed ${
                selectedOption === 'reset-plan'
                  ? 'bg-status-critical-strong hover:bg-status-critical-strong/90'
                  : 'bg-primary hover:bg-primary-800'
              }`}
            >
              {isProcessing ? (
                <div className="flex items-center space-x-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>Processing...</span>
                </div>
              ) : (
                `Continue with ${selectedOption === 'reset-plan' ? 'Reset' : 'Update'}`
              )}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default EnrollmentUpdateWarningModal;
